import { noOp } from 'common/Constants';
import { ClientEvents, MealPlanAnalyticsLocation } from 'common/events/ClientEvents';
import { getWSSPriceFromTypeByRegion } from 'common/meal-plan/ConsumerPrices';
import { isEmpty, isNil } from 'lodash';
import React, { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { DishModalFoodItemFragment, MealPlanFoodItemUpsellFragment } from 'src/gqlReactTypings.generated.d';
import { NutritionMacros } from 'src/pages/consumer/components/DishModalRevamp/NutritionMacros';
import { FavoriteFoodItemButton } from 'src/pages/consumer/favorite-food-item/FavoriteFoodItemButton';
import { useShouldShowFavoriteHeart } from 'src/pages/consumer/favorite-food-item/hooks/useShouldShowFavoriteHeart';
import { MealPlanFoodItem } from 'src/pages/consumer/meal-plans/checkout/types';
import { MerchandisingTagChip } from 'src/pages/consumer/meal-plans/components/Chips/MerchandisingTagChip';
import { CardType } from 'src/pages/consumer/meal-plans/components/MealPlanDishCard';
import { useTracker } from 'src/shared/hooks/useTracker';
import { CartFoodItem } from 'src/store/cart/types';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { getTopKCuisines } from '../../DishFirstCard/utils';
import { Close, Share } from './components';
import { ServingSizeCustomizations } from './components/Customizations/ServingSizeCustomizations';
import { SpiceLevelCustomizations } from './components/Customizations/SpiceLevelCustomizations';
import { ModalReviews } from './components/ModalReviews';
import { ModalV2Footer } from './components/ModalV2Footer';
import { ModalV2InfoContent } from './components/ModalV2InfoContent';
import { DEFAULT_IMAGE, FOOD_IMAGE_PROPS } from './consts';
import { useModalFoodImageParallax } from './hooks/useModalParallax';
import { FoodImage, Modal } from './stylesV2';

interface IBaseDishModalProps {
  canSubmitCart: boolean;
  cardType?: CardType | null;
  collectionId?: string;
  foodItem: CartFoodItem | MealPlanFoodItem;
  formState: IBaseDishModalFormState;
  isEditingCart: boolean;
  isEditingOrder?: boolean;
  isShowing?: boolean;
  loadedData?: DishModalFoodItemFragment;
  location?: string;
  /** Analytics location for in-modal events (e.g. 'favorites-dish-modal'). */
  analyticsLocation?: MealPlanAnalyticsLocation;
  maxQuantity?: number | null;
  minQuantity?: number;
  productType?: 'marketplace' | 'subscriptions';
  servingsPerMeal?: number;
  shareUrl?: string;
  shefMenuUrl?: string;
  showAddButton?: boolean;
  showModalPrompt?: boolean;
  showPortionSizeControls?: boolean;
  unitsInCart: number;
  upsell?: MealPlanFoodItemUpsellFragment | null;
  regionId?: number;
  quantityUnit?: string;
  /** Sits bottom-left on the dish image. Meal plans pass an availability chip; marketplace passes none. */
  bottomLeftChip?: React.ReactNode;
  addButtonText?: string;

  footer?: React.ReactNode;

  // Function props
  onAddToCart?: () => void;
  onClose?: () => void;
  onQuantityChange?: (quantity: number) => void;
  openModal?: () => void;
  imageZoom?: number;
  setFormState: React.Dispatch<React.SetStateAction<IBaseDishModalFormState>>;
  submit: () => void;
}

export interface IBaseDishModalFormState {
  servingSizeCustomizationId?: string | undefined;
  spiceLevelCustomizationId: string;
  quantity: number;
}

export const BaseDishModal: React.FC<React.PropsWithChildren<IBaseDishModalProps>> = (props) => {
  const {
    foodItem,
    loadedData,
    isShowing = false,
    onClose: handleClose = noOp,
    maxQuantity,
    shefMenuUrl,
    shareUrl,
    minQuantity,
    submit,
    canSubmitCart,
    isEditingCart = false,
    showModalPrompt,
    openModal,
    formState,
    setFormState,
    productType = 'marketplace',
    onQuantityChange,
    showPortionSizeControls = true,
    upsell,
    cardType = null,
    servingsPerMeal = 1,
    location = null,
    analyticsLocation = 'dish-modal',
    showAddButton = true,
    collectionId,
    regionId,
    footer,
    quantityUnit,
    bottomLeftChip,
    addButtonText,
    imageZoom = 1,
  } = props;

  const { spiceLevelCustomizations, servingSizeCustomizations } = foodItem;
  const macros = 'macros' in foodItem ? foodItem.macros : undefined;
  const foodItemMacrosEnabled = useFeatureFlag('foodItemMacrosEnabled');
  const shouldRenderMacros = foodItemMacrosEnabled && macros;
  const shouldShowFavoriteHeart = useShouldShowFavoriteHeart();

  const { isSmallScreen } = useScreenContext();
  const { wrapperProps, foodImageContainerProps, foodImageProps, headerProps, headerImageProps, wavyEdgeProps } =
    useModalFoodImageParallax(imageZoom);

  const PortionCustomizationsContainer = isNil(servingSizeCustomizations)
    ? Modal.SectionContainer
    : Modal.GraySectionContainer;
  const SpiceLevelCustomizationsContainer = isNil(spiceLevelCustomizations)
    ? Modal.SectionContainer
    : Modal.GraySectionContainer;

  const tracker = useTracker();

  useEffect(() => {
    if (isShowing) {
      tracker.track(ClientEvents.DISH_MODAL_VISIT, {
        foodItemId: foodItem.id,
        type: 'customizations',
        productType,
        dishServingSizeOptionsCount: servingSizeCustomizations?.length ?? 0,
        dishSpiceLevelOptionsCount: spiceLevelCustomizations?.length ?? 0,
        location,
        collectionId,
        cuisines:
          'cuisineCategories' in foodItem ? foodItem.cuisineCategories.map((c) => c.title).join(', ') : undefined,
      });
    }
    // Intentionally only adding isShowing to dependencies, since this is for resetting the modal
  }, [isShowing]);

  const handleServingSizeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setFormState({ ...formState, servingSizeCustomizationId: e.currentTarget.value });
    },
    [setFormState, formState]
  );

  const handleSpiceLevelChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setFormState({ ...formState, spiceLevelCustomizationId: e.currentTarget.value });
    },
    [setFormState, formState]
  );

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      onQuantityChange?.(newQuantity);
      setFormState({ ...formState, quantity: newQuantity });
    },
    [setFormState, formState]
  );

  const upsellPrice = useMemo(
    () =>
      upsell ? (upsell?.price ?? 0) * formState.quantity * (cardType === CardType.Small ? 1 : servingsPerMeal) : null,
    [formState.quantity, servingsPerMeal, upsell?.price]
  );
  const updatedPrice =
    cardType === CardType.Small || cardType === CardType.SmallWithShefName
      ? (upsellPrice ?? 0) +
        getWSSPriceFromTypeByRegion((foodItem as MealPlanFoodItem).mealPlanFoodType ?? '', regionId ?? 0) *
          formState.quantity
      : upsellPrice;

  const priceToDisplay = updatedPrice;
  const cuisines = !isNil(loadedData) ? getTopKCuisines(loadedData) : undefined;

  return (
    <Modal.Main isShowing={isShowing} onClose={handleClose} isFullHeight={isSmallScreen} isFullWidth={isSmallScreen}>
      <Modal.Wrapper {...wrapperProps}>
        <Modal.Header {...headerProps}>
          <Close onClick={handleClose} />
          <FoodImage.MiniImage
            src={foodItem?.imageUrl ?? DEFAULT_IMAGE}
            alt={foodItem.name ?? FOOD_IMAGE_PROPS.alt}
            {...headerImageProps}
          />
          {shouldShowFavoriteHeart ? (
            <FavoriteFoodItemButton
              foodItemId={foodItem.id}
              analyticsLocation={analyticsLocation}
              dishName={foodItem.name ?? undefined}
              shefId={loadedData?.shef?.id}
              cuisine={loadedData?.cuisineCategories?.[0]?.title ?? null}
            />
          ) : (
            shareUrl && <Share to={shareUrl} />
          )}
        </Modal.Header>
        <FoodImage.Container {...foodImageContainerProps}>
          <FoodImage.Image
            src={foodItem?.imageUrl ?? DEFAULT_IMAGE}
            alt={foodItem.name ?? FOOD_IMAGE_PROPS.alt}
            sourceWidth={Math.ceil(FOOD_IMAGE_PROPS.sourceWidth * imageZoom)}
            sourceHeight={Math.ceil(FOOD_IMAGE_PROPS.sourceHeight * imageZoom)}
            {...foodImageProps}
          />
          {'merchandisingTags' in foodItem && foodItem.merchandisingTags && (
            <div className='absolute bottom-4 right-2'>
              <MerchandisingTagChip merchandisingTags={foodItem.merchandisingTags} />
            </div>
          )}
          {bottomLeftChip && <div className='absolute bottom-4 left-2 empty:hidden'>{bottomLeftChip}</div>}
        </FoodImage.Container>

        <Modal.Body>
          <Modal.TopBodyEdge {...wavyEdgeProps} />
          <ModalV2InfoContent
            foodItem={foodItem}
            shefMenuUrl={shefMenuUrl}
            publicName={loadedData?.shef.publicName}
            cuisines={cuisines}
            averageRating={loadedData?.averageRating}
            upsell={upsell}
            cardType={cardType}
            ratingCount={loadedData?.ratingCount}>
            {props.children}
          </ModalV2InfoContent>
          {showPortionSizeControls && (
            <PortionCustomizationsContainer>
              <ServingSizeCustomizations
                defaultPortion={foodItem.portion}
                defaultServingSize={foodItem.servingSize}
                defaultPrice={foodItem.price}
                servingSizeOptions={servingSizeCustomizations ?? undefined}
                onChange={handleServingSizeChange}
                value={formState.servingSizeCustomizationId}
              />
            </PortionCustomizationsContainer>
          )}
          <SpiceLevelCustomizationsContainer>
            <SpiceLevelCustomizations
              defaultSpiceLevel={foodItem.spiceLevel}
              spiceLevelOptions={spiceLevelCustomizations ?? undefined}
              onChange={handleSpiceLevelChange}
              value={formState.spiceLevelCustomizationId}
            />
          </SpiceLevelCustomizationsContainer>
          {shouldRenderMacros && (
            <div className='-mx-6 bg-sourdough px-6 py-4'>
              <NutritionMacros
                calories={macros.calories}
                protein={macros.protein}
                totalCarbohydrate={macros.totalCarbohydrate}
                totalFat={macros.totalFat}
                sodium={macros.sodium}
              />
            </div>
          )}
          <ModalReviews foodItemId={foodItem.id} ratingData={loadedData} />
        </Modal.Body>

        {showAddButton &&
          (footer ?? (
            <ModalV2Footer
              isEditingCart={isEditingCart}
              quantity={formState.quantity}
              onQuantityChange={handleQuantityChange}
              minQuantity={minQuantity}
              maxQuantity={maxQuantity ?? undefined}
              hasBeenUpdated={canSubmitCart}
              priceToDisplay={priceToDisplay}
              // bit hacky but good enough for now
              isServingSizeSelected={isEmpty(servingSizeCustomizations) || !isNil(formState.servingSizeCustomizationId)}
              actionButtonClick={submit}
              showModalPrompt={showModalPrompt}
              openModal={openModal}
              quantityUnit={quantityUnit}
              addButtonText={addButtonText}
            />
          ))}
      </Modal.Wrapper>
    </Modal.Main>
  );
};
